import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    '[Callro] Missing environment variables.\n' +
    'Copy .env.example to .env and fill in VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  )
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
})

export type Database = {
  public: {
    Tables: {
      agents: { Row: Agent }
      leads: { Row: Lead }
      calls: { Row: Call }
      commissions: { Row: Commission }
      shifts: { Row: Shift }
      callbacks: { Row: Callback }
      system_config: { Row: SystemConfig }
    }
  }
}

export interface Agent {
  id: string
  name: string
  email: string
  agent_code: string
  phone_number: string | null
  telnyx_number: string | null
  telnyx_sip_username: string | null
  telnyx_sip_password: string | null
  role: 'agent' | 'supervisor' | 'admin'
  base_salary_pkr: number
  is_active: boolean
  created_at: string
}

export interface Lead {
  id: string
  phone_number: string
  name: string | null
  source: 'cold_call' | 'website_chat' | 'app_chat' | 'inbound'
  assigned_agent_id: string | null
  status: 'new' | 'contacted' | 'installed' | 'trialing' | 'converted' | 'churned'
  device_id_hash: string | null
  referral_code_used: string | null
  is_founding_member: boolean
  founding_member_number: number | null
  lead_score: number
  notes: string
  sms_opted_out: boolean
  scheduled_callback_at: string | null
  created_at: string
  updated_at: string
}

export interface Call {
  id: string
  lead_id: string | null
  agent_id: string
  direction: 'inbound' | 'outbound'
  source: 'portal'
  duration_seconds: number | null
  recording_url: string | null
  telnyx_recording_url: string | null
  transcript_raw: string | null
  transcript_summary: string | null
  sentiment: 'positive' | 'neutral' | 'negative' | null
  outcome: 'no_answer' | 'voicemail' | 'callback' | 'interested' | 'installed' | 'not_interested' | null
  telnyx_call_id: string | null
  call_control_id: string | null
  consent_announced: boolean
  spanish_mode_active: boolean
  started_at: string
  ended_at: string | null
}

export interface Commission {
  id: string
  agent_id: string
  lead_id: string
  type: 'install' | 'conversion' | 'retention'
  amount_usd: number
  month_year: string
  is_paid: boolean
  paid_at: string | null
  wave_transaction_id: string | null
  created_at: string
}

export interface Shift {
  id: string
  agent_id: string
  shift_date: string
  clock_in: string | null
  clock_out: string | null
  hours_worked: number | null
  notes: string | null
  adjusted_by: string | null
}

export interface Callback {
  id: string
  lead_id: string
  agent_id: string
  scheduled_at: string
  notes: string | null
  status: 'pending' | 'completed' | 'missed' | 'rescheduled'
  created_at: string
}

export interface SystemConfig {
  key: string
  value: boolean
  updated_by: string | null
  updated_at: string
}
